import { motion } from "framer-motion";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { TextField } from "@mui/material";
import * as Yup from "yup";
import Images from "../../Images";
import { useMutation } from "react-query";
import { useDispatch } from "react-redux";
import { setUser } from "../../redux/UserSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import zxcvbn from 'zxcvbn';
import { resetPassword, resendOTP } from "../../requests/auth/Authentication";

// * Request Validation
const validationSchema = Yup.object({
  otp: Yup.string()
    .required("OTP is required")
    .matches(/^[0-9]{4}$/, "OTP must be exactly 4 digits"),
  password: Yup.string()
    .required("Password is required")
    .min(8, "Password must be at least 8 characters long")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character"
    ),
});

const initialValues = {
  otp: "",
  password: "",
};

// Add this component for password strength
const PasswordStrengthIndicator = ({ password }) => {
  const result = zxcvbn(password);
  const strengthScore = result.score; // 0-4

  const getStrengthColor = () => {
    switch (strengthScore) {
      case 0: return 'bg-red-500';
      case 1: return 'bg-orange-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-blue-500';
      case 4: return 'bg-green-500';
      default: return 'bg-gray-200';
    }
  };

  const getStrengthText = () => {
    switch (strengthScore) {
      case 0: return 'Very Weak';
      case 1: return 'Weak';
      case 2: return 'Fair';
      case 3: return 'Good';
      case 4: return 'Strong';
      default: return '';
    }
  };

  return (
    <div className="mt-2">
      <div className="flex gap-2 mb-2">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className={`h-2 w-full rounded-full ${i <= strengthScore ? getStrengthColor() : 'bg-gray-200'}`}
          />
        ))}
      </div>
      <p className={`text-sm ${getStrengthColor().replace('bg-', 'text-')}`}>
        {getStrengthText()}
      </p>
    </div>
  );
};

// Add this component for password requirements
const PasswordRequirements = ({ password }) => {
  const requirements = [
    { regex: /.{8,}/, label: "At least 8 characters" },
    { regex: /[A-Z]/, label: "At least one uppercase letter" },
    { regex: /[a-z]/, label: "At least one lowercase letter" },
    { regex: /[0-9]/, label: "At least one number" },
    { regex: /[@$!%*?&]/, label: "At least one special character" },
  ];

  return (
    <div className="mt-4 space-y-2">
      {requirements.map((req, index) => (
        <div key={index} className="flex items-center gap-2">
          {req.regex.test(password) ? (
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          ) : (
            <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          )}
          <span className={`text-sm ${req.regex.test(password) ? 'text-green-500' : 'text-gray-500'}`}>
            {req.label}
          </span>
        </div>
      ))}
    </div>
  );
};

function PasswordReset() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Add refs for OTP inputs
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [showPassword, setShowPassword] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Add useEffect for initial focus
  useEffect(() => {
    otpRefs[0].current?.focus();
  }, []);

  // Todo => User Registration method
  const resetPasswordMutation = useMutation(
    (values) => resetPassword(values),
    {
      onSuccess: (data) => {
        toast.success("Password reset successfully");
        navigate("/signin");
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const resendOTPMutation = useMutation(
    () => resendOTP({ email: JSON.parse(localStorage.getItem("reset_user_data"))?.email }),
    {
      onSuccess: () => {
        toast.success("OTP resent successfully");
        setIsResending(false);
      },
      onError: (error) => {
        toast.error(error.message);
        setIsResending(false);
      }
    }
  );

  return (
    <div className="h-screen w-full flex flex-col sm:flex-row bg-[#FEFEFE]">
      {/* Left Side */}
      <motion.div
        className="relative hidden w-full h-full sm:w-1/2 sm:flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#FF6C6C] via-[#FF8B8B] to-[#FF9B9B]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Animated Background Elements */}
        <motion.div
          className="absolute w-[800px] h-[800px] bg-white/10 rounded-full blur-xl"
          initial={{ scale: 0, rotate: 0 }}
          animate={{ 
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360],
          }}
          transition={{ 
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <motion.div
          className="absolute z-20 p-4 shadow-lg top-10 right-10 bg-white/30 backdrop-blur-md rounded-2xl"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Vendor Security</span>
          </div>
        </motion.div>

        <motion.div
          className="absolute z-20 p-4 shadow-lg bottom-20 left-10 bg-white/30 backdrop-blur-md rounded-2xl"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          whileHover={{ scale: 1.05 }}
        >
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-white">Business Protection</span>
          </div>
        </motion.div>

        <motion.div
          className="relative z-10 w-4/5 h-4/5"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 1, delay: 0.5 }}
        >
          <motion.div
            className="relative w-full h-full overflow-hidden shadow-2xl rounded-2xl bg-white/10 backdrop-blur-sm"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.3 }}
          >
            <motion.img
              src={Images.register_first}
              alt="reset-password"
              className="object-cover w-full h-full transition-transform duration-700 rounded-2xl hover:scale-110"
            />
            
            {/* Content Overlay */}
            <motion.div 
              className="absolute inset-0 flex flex-col items-center justify-end pb-20 bg-gradient-to-b from-black/10 via-black/20 to-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              <motion.div className="px-8 text-center">
                <motion.h2 
                  className="mb-6 text-5xl font-bold text-white drop-shadow-lg"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.2 }}
                >
                  Reset Password
                </motion.h2>
                <motion.p 
                  className="max-w-md mx-auto text-xl text-white/90"
                  initial={{ y: 20 }}
                  animate={{ y: 0 }}
                  transition={{ delay: 1.4 }}
                >
                  Create a new secure password for your account
                </motion.p>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div 
        className="w-full px-8 pt-16 pb-8 overflow-y-auto sm:w-1/2 sm:h-screen sm:px-16"
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Logo */}
        <motion.div
          className="mb-6"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <img src={Images.logo} alt="Dealboxx Logo" className="w-32 h-auto"/>
        </motion.div>

        {/* Back Button */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <motion.button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#808080] hover:text-[#FF6C6C] transition-colors duration-300"
            whileHover={{ x: -5 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="w-5 h-5" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
            <span className="font-medium">Back</span>
          </motion.button>
        </motion.div>

        {/* Title and Description */}
        <motion.div
          className="mb-8 space-y-3"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <h1 className="text-3xl font-bold bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] bg-clip-text text-transparent">
            Reset Vendor Password
          </h1>
          <p className="text-[#808080] text-base">
            Create a strong password for your vendor account. Ensure it meets all security requirements.
          </p>
        </motion.div>

        {/* Form */}
        <Formik
          initialValues={{ ...initialValues, otp: "" }}
          validationSchema={validationSchema}
          onSubmit={values => resetPasswordMutation.mutate(values)}
        >
          {({ values, handleSubmit, setFieldValue, isSubmitting }) => {
            const handleOtpChange = (index, value) => {
              if (value.length <= 1) {
                const newOtp = [...values.otp];
                newOtp[index] = value;
                setFieldValue('otp', newOtp.join(''));
                
                // Move to next input if value is entered
                if (value && index < 3) {
                  otpRefs[index + 1].current?.focus();
                }
              }
            };

            const handleKeyDown = (index, e) => {
              if (e.key === 'Backspace' && !values.otp[index] && index > 0) {
                otpRefs[index - 1].current?.focus();
              }
            };

            return (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
              >
                <Form className="space-y-6">
                  {/* Replace OTP TextField with new boxes */}
                  <motion.div 
                    whileHover={{ scale: 1.01 }} 
                    className="space-y-4"
                  >
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Enter 4-digit OTP
                    </label>
                    <div className="flex gap-4 justify-center">
                      {[0, 1, 2, 3].map((index) => (
                        <motion.input
                          key={index}
                          ref={otpRefs[index]}
                          type="text"
                          maxLength={1}
                          value={values.otp[index] || ''}
                          onChange={(e) => handleOtpChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          className="w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 focus:border-[#FF6C6C] focus:ring-2 focus:ring-[#FF6C6C]/20 outline-none transition-all"
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            delay: 0.1 * index,
                            type: "spring",
                            stiffness: 300,
                            damping: 20
                          }}
                          whileFocus={{
                            scale: 1.05,
                            borderColor: "#FF6C6C",
                            boxShadow: "0 0 0 4px rgba(255,108,108,0.2)"
                          }}
                        />
                      ))}
                    </div>
                    <ErrorMessage name="otp" component="div" className="text-sm text-red-500" />
                  </motion.div>

                  <div className="flex justify-end mt-2">
                    <motion.button
                      type="button"
                      onClick={() => resendOTPMutation.mutate()}
                      disabled={isResending}
                      className="text-sm text-[#FF6C6C] hover:text-[#FF8B8B] disabled:text-gray-400"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <div className="flex items-center gap-2">
                        {isResending && (
                          <motion.div
                            className="w-3 h-3 border-2 border-[#FF6C6C] border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                        )}
                        {isResending ? "Resending..." : "Resend OTP"}
                      </div>
                    </motion.button>
                  </div>

                  {/* Password Field with Toggle */}
                  <motion.div whileHover={{ scale: 1.01 }} className="space-y-4">
                    <Field
                      name="password"
                      as={TextField}
                      label="New Password"
                      type={showPassword ? "text" : "password"}
                      variant="outlined"
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => setShowPassword(!showPassword)}
                            className="cursor-pointer text-gray-500 hover:text-[#FF6C6C]"
                          >
                            {showPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                                <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                              </svg>
                            )}
                          </motion.div>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          backgroundColor: 'white',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          },
                          '&.Mui-focused': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 4px 12px rgba(255,108,108,0.2)',
                            '& fieldset': {
                              borderColor: '#FF6C6C',
                              borderWidth: '2px',
                            }
                          }
                        },
                        '& .MuiInputLabel-root': {
                          '&.Mui-focused': {
                            color: '#FF6C6C'
                          }
                        }
                      }}
                    />
                    <ErrorMessage name="password" component="div" className="text-sm text-red-500" />
                    
                    {/* Password Strength and Requirements */}
                    <PasswordStrengthIndicator password={values.password} />
                    <PasswordRequirements password={values.password} />
                  </motion.div>

                  {/* Submit Button with Loading State */}
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-70"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center justify-center gap-2">
                      {isSubmitting && (
                        <motion.div
                          className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      )}
                      {isSubmitting ? "Resetting Password..." : "Reset Password"}
                    </div>
                  </motion.button>
                </Form>
              </motion.div>
            );
          }}
        </Formik>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-3 gap-3 mt-12"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.3 }}
        >
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔒</div>
              <div className="text-xs text-gray-600">Vendor Security</div>
            </motion.div>
          </div>
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">🔐</div>
              <div className="text-xs text-gray-600">Business Data</div>
            </motion.div>
          </div>
          <div className="p-3 text-center transition-shadow duration-300 bg-white shadow-sm rounded-xl hover:shadow-md">
            <motion.div whileHover={{ scale: 1.05 }}>
              <div className="text-[#FF6C6C] text-lg mb-1">💼</div>
              <div className="text-xs text-gray-600">Account Access</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default PasswordReset;
